import React, { useState } from "react";
import api, { fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Hammer } from "lucide-react";
import { fmtGbp } from "@/lib/constants";

export function MWThresholdPanel({ engagement, onSaved, locked }) {
    const [f, setF] = useState({
        units: engagement.mw_units ?? "",
        mw_per_unit: engagement.mw_per_unit ?? 250,
        qlta_per_unit: engagement.qlta_per_unit ?? 100,
    });
    const units = Number(f.units) || 0;
    const mwPer = Number(f.mw_per_unit) || 0;
    const qltaPer = Number(f.qlta_per_unit) || 0;
    const mw = units * mwPer;
    const qlta = units * qltaPer;
    const dirty = engagement.mw_threshold_gbp !== mw || engagement.mw_units !== (units || null) || engagement.qlta_threshold_gbp !== qlta;

    const save = async () => {
        try {
            await api.patch(`/engagements/${engagement.id}`, {
                mw_units: units || null, mw_per_unit: mwPer, mw_threshold_gbp: mw,
                qlta_per_unit: qltaPer, qlta_threshold_gbp: qlta,
            });
            toast.success("Major-works threshold saved to the engagement");
            onSaved?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    const inp = "border border-input rounded-sm px-2 py-1.5 text-sm font-num bg-white w-full disabled:bg-slate-50";

    return (
        <div className="bg-white border-2 border-brand/40 rounded-sm mb-5" data-testid="mw-threshold-panel">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
                <Hammer className="w-4 h-4 text-brand" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-display font-semibold">Section 20 major-works threshold · reminds D1 &amp; E2 to check qualifying works</span>
            </div>
            <div className="p-4 grid grid-cols-3 gap-4 items-end">
                <L label="Number of units (leaseholders)"><input data-testid="mw-units" type="number" disabled={locked} value={f.units} onChange={(e) => setF({ ...f, units: e.target.value })} className={inp} /></L>
                <L label="Major works £ per leaseholder" hint="Section 20 statutory · default £250 incl VAT"><input data-testid="mw-per-unit" type="number" disabled={locked} value={f.mw_per_unit} onChange={(e) => setF({ ...f, mw_per_unit: e.target.value })} className={inp} /></L>
                <L label="QLTA £ per leaseholder / year" hint="Long-term agreements > 12 months · default £100 pa incl VAT"><input data-testid="mw-qlta-per-unit" type="number" disabled={locked} value={f.qlta_per_unit} onChange={(e) => setF({ ...f, qlta_per_unit: e.target.value })} className={inp} /></L>
            </div>
            <div className="px-4 pb-4 flex items-center gap-8">
                <Out label="Major works threshold (units × £250)" v={mw} testid="mw-threshold" />
                <Out label="QLTA threshold (units × £100 pa)" v={qlta} testid="mw-qlta-threshold" />
                {!locked && <button data-testid="mw-save-btn" onClick={save} disabled={!units} className="ml-auto bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-1.5 rounded-sm disabled:opacity-40">{dirty ? "Save threshold" : "Saved ✓"}</button>}
            </div>
        </div>
    );
}

function L({ label, hint, children }) {
    return <div><label className="text-[10px] uppercase tracking-wider text-muted-foreground font-display block mb-1 leading-tight">{label}</label>{children}{hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}</div>;
}
function Out({ label, v, testid }) {
    return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">{label}</div><div className="font-display font-bold text-xl font-num" data-testid={testid}>{v ? fmtGbp(v) : "—"}</div></div>;
}
